import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MOTIVO_OPTIONS,
  NIVEL_ACIONAMENTO_OPTIONS,
  TIPO_INTERDICAO_OPTIONS,
  normalizeText,
  pad2,
  parseYMD,
  toBRDate,
  toTodayYMD,
} from "../../../core/cimInterdicoes.utils";
import InterdicaoViaFields from "./tipos/InterdicaoViaFields";
import InterdicaoToFields from "./tipos/InterdicaoToFields";
import InterdicaoLocomotivasFields from "./tipos/InterdicaoLocomotivasFields";
import InterdicaoVagoesFields from "./tipos/InterdicaoVagoesFields";
import "./InterdicaoNovaModal.css";

const DATE_WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1, 12, 0, 0);
}

function monthLabel(date) {
  const label = new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function buildMonthGrid(viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthStart = new Date(year, month, 1, 12, 0, 0);
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - monthStart.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return day;
  });
}

function emitSyntheticChange(onChange, nextValue) {
  onChange?.({ target: { value: nextValue } });
}

function Section({ title, children, className = "" }) {
  return (
    <section className={`interdNova-section ${className}`.trim()}>
      {title ?<div className="interdNova-sectionTitle">{title}</div> : null}
      {children}
    </section>
  );
}

function TipoInterdicaoField({ value, onChange, options = TIPO_INTERDICAO_OPTIONS }) {
  return (
    <div className="interdNova-field interdNova-field--full interdNova-field--typeNav">
      <div className="interdNova-typeGrid" role="radiogroup" aria-label="Aplicação da interdição">
        {options.map((option) => {
          const selected = value === option;
          return (
            <button
              key={option}
              type="button"
              className={`interdNova-typeBtn ${selected ?"is-selected" : ""}`.trim()}
              onClick={() => emitSyntheticChange(onChange, option)}
              aria-pressed={selected}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DropdownField({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Selecione...",
  disabled = false,
  preferredDirection = "auto",
  searchable = false,
  searchInline = false,
  searchPlaceholder = "Digite para buscar...",
  menuMaxHeight = 220,
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const resolvedMenuMaxHeight = Math.max(56, Number(menuMaxHeight) || 220);

  const normalizedOptions = useMemo(
    () =>
      (Array.isArray(options) ?options : [])
        .map((option) =>
          typeof option === "object"
            ? {
                value: String(option?.value ?? option?.label ?? "").trim(),
                label: String(option?.label ?? option?.value ?? "").trim(),
              }
            : {
                value: String(option ?? "").trim(),
                label: String(option ?? "").trim(),
              }
        )
        .filter((option) => option.value),
    [options]
  );

  const selectedOption = useMemo(
    () => normalizedOptions.find((option) => option.value === String(value ?? "")) || null,
    [normalizedOptions, value]
  );

  const filteredOptions = useMemo(() => {
    if (!searchable || !query.trim()) return normalizedOptions;
    const normalizedQuery = normalizeText(query);
    return normalizedOptions.filter((option) =>
      normalizeText(`${option.label} ${option.value}`).includes(normalizedQuery)
    );
  }, [normalizedOptions, query, searchable]);

  const closeMenu = () => {
    const activeEl = typeof document !== "undefined" ?document.activeElement : null;
    if (activeEl && rootRef.current?.contains(activeEl) && typeof activeEl.blur === "function") {
      activeEl.blur();
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) closeMenu();
    };

    const handleEsc = (event) => {
      if (event.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  useEffect(() => {
    if (searchable && searchInline) {
      if (!open) setQuery(selectedOption?.label || "");
      return undefined;
    }

    if (!open) {
      setQuery("");
      return undefined;
    }

    if (searchable) {
      window.setTimeout(() => searchRef.current?.focus(), 0);
    }

    return undefined;
  }, [open, searchable, searchInline, selectedOption]);

  useEffect(() => {
    if (!open || !searchable || !searchInline) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open, searchable, searchInline]);

  useEffect(() => {
    if (!open) return undefined;

    const updateDirection = () => {
      if (preferredDirection === "up") {
        setOpenUp(true);
        return;
      }

      if (preferredDirection === "down") {
        setOpenUp(false);
        return;
      }

      const rootEl = rootRef.current;
      if (!rootEl) return;

      const rect = rootEl.getBoundingClientRect();
      const searchHeight = searchable && !searchInline ?48 : 0;
      const menuHeight =
        Math.min(Math.max(normalizedOptions.length * 40, 56), resolvedMenuMaxHeight) +
        searchHeight +
        8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      setOpenUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
    };

    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);

    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [
    normalizedOptions.length,
    open,
    preferredDirection,
    resolvedMenuMaxHeight,
    searchable,
    searchInline,
  ]);

  return (
    <label className="interdNova-field">
      <span>{label}</span>
      <div
        ref={rootRef}
        className={`interdNova-selectRoot ${open ?"is-open" : ""} ${openUp ?"is-up" : ""} ${
          disabled ?"is-disabled" : ""
        } ${searchable && searchInline ?"is-inline-search" : ""}`.trim()}
        style={{ "--interdNova-select-menu-max-height": `${resolvedMenuMaxHeight}px` }}
      >
        {searchable && searchInline ?(
          open ?(
            <div
              className="interdNova-selectTrigger interdNova-selectTriggerSearch interdNova-selectTriggerEditing"
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={open}
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  event.preventDefault();
                  closeMenu();
                  inputRef.current?.blur();
                }
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={query}
                disabled={disabled}
                className={`interdNova-selectInlineInput ${
                  selectedOption || query ?"" : "interdNova-selectPlaceholder"
                }`.trim()}
                placeholder={searchPlaceholder || placeholder}
                onMouseDown={(event) => {
                  if (document.activeElement === inputRef.current) {
                    event.preventDefault();
                    closeMenu();
                    inputRef.current?.blur();
                  }
                }}
                onChange={(event) => {
                  if (!disabled) setQuery(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (disabled) return;
                  if (event.key === "Escape") {
                    setQuery(selectedOption?.label || "");
                    closeMenu();
                    inputRef.current?.blur();
                  }
                }}
              />
            </div>
          ) : (
            <button
              type="button"
              className="interdNova-selectTrigger"
              onClick={() => {
                if (!disabled) setOpen(true);
              }}
              disabled={disabled}
              aria-haspopup="listbox"
              aria-expanded={open}
            >
              <span
                className={`interdNova-selectValue ${
                  selectedOption ?"" : "interdNova-selectPlaceholder"
                }`.trim()}
              >
                {selectedOption?.label || searchPlaceholder || placeholder}
              </span>
            </button>
          )
        ) : (
          <button
            type="button"
            className="interdNova-selectTrigger"
            onClick={() => {
              if (!disabled) setOpen((current) => !current);
            }}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span
              className={`interdNova-selectValue ${
                selectedOption ?"" : "interdNova-selectPlaceholder"
              }`.trim()}
            >
              {selectedOption?.label || placeholder}
            </span>
          </button>
        )}

        <div
          className={`interdNova-selectMenu ${openUp ?"interdNova-selectMenuUp" : ""}`.trim()}
          role="listbox"
          aria-hidden={!open}
        >
          {searchable && !searchInline ?(
            <div className="interdNova-selectSearchWrap">
              <input
                ref={searchRef}
                type="text"
                className="interdNova-selectSearch"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
              />
            </div>
          ) : null}

          {filteredOptions.map((option) => {
            const selected = selectedOption?.value === option.value;
            return (
              <button
                type="button"
                key={option.value}
                className={`interdNova-selectOption ${selected ?"is-selected" : ""}`.trim()}
                onClick={() => {
                  emitSyntheticChange(onChange, option.value);
                  setQuery(option.label);
                  closeMenu();
                }}
                aria-selected={selected}
              >
                {option.label}
              </button>
            );
          })}

          {!filteredOptions.length ?(
            <div className="interdNova-selectEmpty">Nenhuma seção encontrada.</div>
          ) : null}
        </div>
      </div>
    </label>
  );
}

function DateDropdownField({
  label,
  value,
  onChange,
  placeholder = "Selecione a data",
  disabled = false,
  preferredDirection = "auto",
}) {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const selectedDate = useMemo(
    () => parseYMD(value) || parseYMD(toTodayYMD()) || new Date(),
    [value]
  );
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [viewMonth, setViewMonth] = useState(
    () => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1, 12, 0, 0)
  );

  useEffect(() => {
    if (!open) {
      setViewMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1, 12, 0, 0));
    }
  }, [open, value, selectedDate]);

  const closePanel = ({ restoreFocus = false } = {}) => {
    const activeEl = typeof document !== "undefined" ?document.activeElement : null;
    if (activeEl && rootRef.current?.contains(activeEl) && typeof activeEl.blur === "function") {
      activeEl.blur();
    }
    setOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    }
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) closePanel();
    };

    const handleEsc = (event) => {
      if (event.key === "Escape") closePanel({ restoreFocus: true });
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setOpenUp(false);
      return undefined;
    }

    const updateDirection = () => {
      if (preferredDirection === "up") {
        setOpenUp(true);
        return;
      }

      if (preferredDirection === "down") {
        setOpenUp(false);
        return;
      }

      const rootEl = rootRef.current;
      if (!rootEl) return;

      const rect = rootEl.getBoundingClientRect();
      const panelHeight = 332;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      setOpenUp(spaceBelow < panelHeight && spaceAbove > spaceBelow);
    };

    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);

    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [open, preferredDirection]);

  const calendarDays = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);
  const today = toTodayYMD();
  const currentMonth = viewMonth.getMonth();

  const handlePick = (ymd) => {
    emitSyntheticChange(onChange, ymd);
    closePanel({ restoreFocus: true });
  };

  return (
    <label className="interdNova-field">
      <span>{label}</span>
      <div
        ref={rootRef}
        className={`interdNova-dateRoot ${open ?"is-open" : ""} ${openUp ?"is-up" : ""} ${
          disabled ?"is-disabled" : ""
        }`.trim()}
      >
        <button
          ref={triggerRef}
          type="button"
          className="interdNova-selectTrigger interdNova-dateButton"
          onClick={() => {
            if (!disabled) {
              if (open) closePanel();
              else setOpen(true);
            }
          }}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span
            className={`interdNova-dateButtonValue ${
              value ?"" : "interdNova-selectPlaceholder"
            }`.trim()}
          >
            {value ?toBRDate(value) : placeholder}
          </span>
        </button>

        <div className="interdNova-datePanel" role="dialog" aria-hidden={!open}>
          <div className="interdNova-datePanelHead">
            <button
              type="button"
              className="interdNova-dateNav"
              onClick={() => setViewMonth((current) => addMonths(current, -1))}
              aria-label="Mês anterior"
            >
              {"<"}
            </button>
            <div className="interdNova-datePanelTitle">{monthLabel(viewMonth)}</div>
            <button
              type="button"
              className="interdNova-dateNav"
              onClick={() => setViewMonth((current) => addMonths(current, 1))}
              aria-label="Próximo mês"
            >
              {">"}
            </button>
          </div>

          <div className="interdNova-dateWeekdays">
            {DATE_WEEKDAYS.map((weekday, index) => (
              <span key={`${weekday}-${index}`} className="interdNova-dateWeekday">
                {weekday}
              </span>
            ))}
          </div>

          <div className="interdNova-dateGrid">
            {calendarDays.map((day) => {
              const dayKey = `${day.getFullYear()}-${pad2(day.getMonth() + 1)}-${pad2(day.getDate())}`;
              const isOutside = day.getMonth() !== currentMonth;
              const isSelected = dayKey === value;
              const isToday = dayKey === today;

              return (
                <button
                  type="button"
                  key={dayKey}
                  className={[
                    "interdNova-dateDay",
                    isOutside ?"is-outside" : "",
                    isSelected ?"is-selected" : "",
                    isToday ?"is-today" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handlePick(dayKey)}
                  aria-pressed={isSelected}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

        </div>
      </div>
    </label>
  );
}

export default function InterdicaoNovaModal({
  open,
  form,
  onClose,
  onSubmit,
  onChange,
  canSubmit,
  saving = false,
  saveError = "",
  sbOptions = [],
  motivoOptionsVia = MOTIVO_OPTIONS,
  motivoOptionsLocomotivas = [],
  motivoOptionsTo = [],
  motivoOptionsVagoes = [],
  tipoOperacaoOptions = [],
  postoOrigemSosOptions = [],
  tipoOptions = TIPO_INTERDICAO_OPTIONS,
  kmHint = "",
  kmError = "",
}) {
  if (!open) return null;

  const fieldProps = {
    Section,
    DropdownField,
    DateDropdownField,
    form,
    onChange,
    sbOptions,
    motivoOptions:
      Array.isArray(motivoOptionsVia) && motivoOptionsVia.length ?motivoOptionsVia : MOTIVO_OPTIONS,
    motivoOptionsLocomotivas,
    motivoOptionsTo,
    motivoOptionsVagoes,
    tipoOperacaoOptions,
    postoOrigemSosOptions,
    nivelAcionamentoOptions: NIVEL_ACIONAMENTO_OPTIONS,
    kmHint,
    kmError,
  };

  const renderTipoFields = () => {
    switch (form.tipoInterdicao) {
      case "TO":
        return <InterdicaoToFields {...fieldProps} />;
      case "LOCOMOTIVAS":
        return <InterdicaoLocomotivasFields {...fieldProps} />;
      case "VAGÕES":
        return <InterdicaoVagoesFields {...fieldProps} />;
      case "VIA":
      default:
        return <InterdicaoViaFields {...fieldProps} />;
    }
  };

  return (
    <div className="interdNova-backdrop" role="presentation">
      <div
        className="interdNova-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="interdNova-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="interdNova-header">
          <div id="interdNova-title" className="interdNova-title">
            Nova Interdição
          </div>

          <button
            className="interdNova-close"
            type="button"
            onClick={onClose}
            aria-label="Fechar cadastro"
          >
            ×
          </button>
        </div>

        <form className="interdNova-shell" onSubmit={onSubmit}>
          <div className="interdNova-content">
            <Section className="interdNova-section--typeNav">
              <TipoInterdicaoField
                value={form.tipoInterdicao}
                onChange={onChange("tipoInterdicao")}
                options={tipoOptions}
              />
            </Section>
            <div className="interdNova-main">
              {renderTipoFields()}
            </div>
          </div>

          <div className="interdNova-actions">
            {saveError ?<div className="interdNova-fieldError">{saveError}</div> : null}
            <button
              className="interdNova-btn interdNova-btn--primary"
              type="submit"
              disabled={saving}
            >
              {saving ?"Cadastrando..." : "Cadastrar interdição"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
